import { useState, type FormEvent } from "react";
import { ResourceCards } from "../resource-cards";
import { confirmForClient, makeSafeT } from "./core";
import type { StaffPaneProps } from "./console-types";
export function ResourcesPane(props: StaffPaneProps & { includeKeys?: readonly string[] }) {
  const allowedKeys = props.includeKeys ?? resourceKeys;
  const [key, setKey] = useState(allowedKeys[0] ?? "url_doc");
  const [json, setJson] = useState('{"url":"https://"}');
  const [visible, setVisible] = useState(true);
  async function save(event: FormEvent) {
    event.preventDefault();
    let value: unknown;
    try { value = JSON.parse(json); } catch { return; }
    await props.run(`/api/admin/clients/${props.detail.client.id}/resources/${key}`, { method: "PUT", body: { value, visible } });
  }
  const items = props.includeKeys
    ? props.detail.resources.filter((item) => props.includeKeys!.includes(String(item.key)))
    : props.detail.resources;
  return <div className="detail-grid"><ResourceCards items={items} role={props.role} t={props.t} locale={props.locale} />{props.role === "admin" && (
    <form className="staff-card editor-form" onSubmit={save}>
      <h3>{props.t("staffConsole.resourceEditor")}</h3>
      <select value={key} onChange={(event) => setKey(event.target.value)}>{allowedKeys.map((item) => <option key={item} value={item}>{makeSafeT(props.t)(`staffConsole.resourceName.${item}`, item.replaceAll("_", " "))}</option>)}</select>
      <textarea value={json} onChange={(event) => setJson(event.target.value)} rows={6} />
      <label className="checkbox-row"><input type="checkbox" checked={visible} onChange={(event) => setVisible(event.target.checked)} />{props.t("staffConsole.visible")}</label>
      <div className="button-row"><button className="primary-button" type="submit">{props.t("staffConsole.save")}</button><button className="text-button danger" type="button" onClick={() => { if (confirmForClient(props, "staffConsole.confirmResourceDelete")) void props.run(`/api/admin/clients/${props.detail.client.id}/resources/${key}`, { method: "DELETE" }); }}>{props.t("staffConsole.delete")}</button></div>
    </form>
  )}</div>;
}

// Stripe connect link (meeting 24.7. D9): Alex generates the link on
// command, the partner pastes it here, and the client's Stripe step shows
// it as the "Připojit Stripe" button. There is no client-side TTL; replace
// the link only after a provider rejection, or clear it when needed.
export function StripeConnectCard(props: StaffPaneProps) {
  const stripeResource = props.detail.resources.find((row) => String(row.key) === "stripe_setup");
  const value = (stripeResource && typeof stripeResource.value === "object" && stripeResource.value !== null
    ? stripeResource.value
    : {}) as Record<string, unknown>;
  const current = typeof value.connect_url === "string" ? value.connect_url : "";
  const [url, setUrl] = useState(current);
  const [state, setState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  async function save(next: string) {
    setState("saving");
    const response = await props.run(`/api/staff/clients/${props.detail.client.id}/stripe-connect-url`, {
      method: "PUT",
      body: { url: next }
    });
    setState(response.ok ? "saved" : "error");
  }

  return (
    <article className="staff-card stripe-connect-card">
      <h3>{props.t("staffConsole.stripeConnect.title")}</h3>
      <p className="staff-card-hint">{props.t("staffConsole.stripeConnect.hint")}</p>
      {/* F-B (meeting D9/D10): the client is promised the page gets its
          payment links after the tariffs exist. This is the partner's half
          of that promise - the loop runs through the existing edit flow. */}
      <p className="staff-card-hint">{props.t("staffConsole.stripeConnect.afterTariffs")}</p>
      {current && (
        <p className="staff-card-hint break-text">
          {props.t("staffConsole.stripeConnect.current")} <a href={current} target="_blank" rel="noreferrer">{current}</a>
        </p>
      )}
      <div className="inline-fields">
        <input
          value={url}
          onChange={(event) => { setUrl(event.target.value); setState("idle"); }}
          placeholder="https://connect.stripe.com/..."
          aria-label={props.t("staffConsole.stripeConnect.title")}
        />
        <button
          className="secondary-button"
          type="button"
          disabled={state === "saving" || !url.trim() || url.trim() === current}
          onClick={() => void save(url.trim())}
        >
          {props.t("staffConsole.save")}
        </button>
        {current && (
          <button
            className="text-button danger"
            type="button"
            disabled={state === "saving"}
            onClick={() => { setUrl(""); void save(""); }}
          >
            {props.t("staffConsole.stripeConnect.clear")}
          </button>
        )}
      </div>
      {state === "saved" && <p className="message success-message" role="status">{props.t("staffConsole.stripeConnect.saved")}</p>}
      {state === "error" && <p className="message error-message" role="alert">{props.t("common.error")}</p>}
    </article>
  );
}

export const resourceKeys = ["drive_folder", "url_doc", "trial_link", "license", "bot_transfer_state", "app_state", "stripe_setup", "handover_telegram", "onboarding_video", "marketing_video"];
export function resourceKeysForWorkspace(kind: "knowledge"): readonly string[] {
  return kind === "knowledge" ? ["drive_folder", "url_doc"] : resourceKeys;
}
